import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/services/api'
import { useDealsStore } from '@/stores/useDealsStore'
import { useAuthStore } from '@/stores/authStore'
import { mapDealsFromApi } from '@/adapters/deals'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { canFilterDealsByCloser } from '@/utils/dealsTeamAccess'

export const DEALS_PAGE_SIZE = 50

function buildDealsListParams(page: number, closerFilter: string, userRole?: string | null) {
  const params: Record<string, string | number> = {
    page,
    limit: DEALS_PAGE_SIZE,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  }

  if (canFilterDealsByCloser(userRole) && closerFilter !== 'all') {
    params.closerId = closerFilter
  }

  return params
}

export type DealsListMeta = {
  page: number
  totalPages: number
  totalItems: number
}

export function useDealsListFetch() {
  const [searchParams] = useSearchParams()
  const closerFilter = useDealsStore((state) => state.closerFilter)
  const userRole = useAuthStore((state) => state.user?.role)
  const { setDeals, appendDealsPage, setLoading, setSelectedDeal } = useDealsStore()
  const [dealsMeta, setDealsMeta] = useState<DealsListMeta>({ page: 1, totalPages: 1, totalItems: 0 })
  const [loadingMore, setLoadingMore] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const fetchDeals = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const resp = await api.get('/deals', {
          params: buildDealsListParams(1, closerFilter, userRole),
        })
        const items = mapDealsFromApi(resp.data.data ?? resp.data ?? [])
        const meta = resp.data.meta
        setDeals(items)
        setDealsMeta({
          page: meta?.page ?? 1,
          totalPages: meta?.totalPages ?? 1,
          totalItems: meta?.totalItems ?? items.length,
        })
        setListError(null)

        const urlId = searchParams.get('id')
        if (urlId && items.length > 0 && !silent) {
          const dealToOpen = items.find((d) => d.id === urlId || d.leadId === urlId)
          if (dealToOpen) setSelectedDeal(dealToOpen)
        }
      } catch (err: unknown) {
        setDeals([])
        setDealsMeta({ page: 1, totalPages: 1, totalItems: 0 })
        const message = getFriendlyError(err, 'Erro ao carregar deals')
        setListError(message)
        if (!silent) toast.error(message)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [closerFilter, setDeals, setLoading, searchParams, setSelectedDeal, userRole],
  )

  const loadMoreDeals = useCallback(async () => {
    if (loadingMore || dealsMeta.page >= dealsMeta.totalPages) return
    setLoadingMore(true)
    try {
      const nextPage = dealsMeta.page + 1
      const resp = await api.get('/deals', {
        params: buildDealsListParams(nextPage, closerFilter, userRole),
      })
      const items = mapDealsFromApi(resp.data.data ?? resp.data ?? [])
      const meta = resp.data.meta
      appendDealsPage(items)
      setDealsMeta({
        page: meta?.page ?? nextPage,
        totalPages: meta?.totalPages ?? dealsMeta.totalPages,
        totalItems: meta?.totalItems ?? dealsMeta.totalItems,
      })
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao carregar mais deals'))
    } finally {
      setLoadingMore(false)
    }
  }, [appendDealsPage, closerFilter, dealsMeta, loadingMore, userRole])

  const hasMoreDeals = dealsMeta.page < dealsMeta.totalPages

  return {
    dealsMeta,
    hasMoreDeals,
    loadingMore,
    listError,
    fetchDeals,
    loadMoreDeals,
  }
}
